import React, { useRef, useEffect, useLayoutEffect, useState, useMemo } from "react";
import {
  buildHighwayTimeLayout,
  computeMastilRelativeBlockWidths,
  fretboardVisualDivision,
  getMeasureLengthDivisions,
  layoutMastilBlocksInStaffSlots,
  type MeasureInfo,
  type PositionedNote,
} from "../lib/songEngine";
import { staffPixelXAtTime } from "../lib/songPlayback";
import { SATURATED_DEGREE_HEX } from "../lib/scaleModuleTheme";
import { LUTHERIE_PALETTE, LUTHERIE_STRING_MATERIALS } from "../lib/fretboardLutherieTheme";

// ═══════════════════════════════════════════════════
// 🎻 PALETA LUTHERÍA ARTESANAL — Bordona (Canciones)
// ═══════════════════════════════════════════════════
const PALETTE = LUTHERIE_PALETTE;
const STRING_MATERIALS = LUTHERIE_STRING_MATERIALS;

const CUERDAS = 6;
const MARGEN_IZQ = 64;
const MARGEN_DER = 48;
const MARGEN_TOP = 12;
const MARGEN_BOT = 12;
const CUERDA_SPACING = 44;

// ═══════════════════════════════════════════════════
// 🎨 COLORES POR GRADO DE ESCALA (I = acento módulo Escalas)
// ═══════════════════════════════════════════════════
const DEGREE_COLORS: readonly string[] = [...SATURATED_DEGREE_HEX];
const OPEN_NOTES = ["E", "B", "G", "D", "A", "E"];

interface SongFretboardFlowProps {
  notes: PositionedNote[];
  totalWidth: number;
  divisions: number;
  timeSignature?: { beats: number; beatType: number };
  currentNoteIndex?: number;
  activeStartTime?: number;
  pixelsPerBeat?: number;
  onNoteClick?: (note: PositionedNote, index: number) => void;
  staffFirstNoteX?: number;
  hidePlayhead?: boolean;
  darkMode?: boolean;
  measureInfos?: MeasureInfo[];
  /**
   * Muestras (startTime → x en px del SVG del pentagrama): mismas proporciones que el VexFlow.
   */
  staffTimeGeometry?: Array<{ startTime: number; x: number }>;
  /** Misma ancla que SongStudio / playback (primer ataque real). */
  scrollAnchorStartTime?: number;
  /**
   * contentX del timeline (highway) en el primer ataque real; alinea bloques con mediciones del pentagrama.
   */
  /** Ancho scroll del pentagrama (Vex); alinea el máximo de scroll del mástil. */
  staffScrollContentWidth?: number;
  /** Ancho en px del “riel” izquierdo igual al del pentagrama (clave + cifra hasta el primer contenido rítmico). */
  notationLeadPx?: number;
  /** Durante scroll: CSS `.highway-scroll-active` atenua filtros (ver estilos abajo). */
}

const VIEWPORT_PAD_PX = 520;
const VIEWPORT_SYNC_EPS_PX = 160;

const SongFretboardFlow = (props: SongFretboardFlowProps & { _ref?: React.RefObject<HTMLDivElement | null> }) => {
    const {
      notes,
      totalWidth,
      divisions,
      timeSignature = { beats: 4, beatType: 4 },
      currentNoteIndex = -1,
      activeStartTime = -1,
      pixelsPerBeat = 80,
      onNoteClick,
      _ref,
      staffFirstNoteX,
      hidePlayhead = false,
      darkMode = false,
      measureInfos = [],
      staffTimeGeometry,
      scrollAnchorStartTime = 0,
      highwayContentAnchorX = 0,
      notationLeadPx,
      staffScrollContentWidth,
    } = props;
    const scrollRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
      if (_ref) (_ref as React.MutableRefObject<HTMLDivElement | null>).current = scrollRef.current;
    });
    const [hoveredNote, setHoveredNote] = useState<number | null>(null);
    const [viewRange, setViewRange] = useState({ minX: 0, maxX: 2400 });

  const TRASTE_SPACING = pixelsPerBeat;
  const mastilHeight = MARGEN_TOP + CUERDAS * CUERDA_SPACING + MARGEN_BOT;

  const measureLengthDiv = getMeasureLengthDivisions(
    timeSignature.beats,
    timeSignature.beatType,
    divisions
  );
  const measureWidthPx = (measureLengthDiv / divisions) * TRASTE_SPACING;

  /** Riel izquierdo = pausa visual equivalente a clave + cifra en el pentagrama. */
  const leftRailPx = notationLeadPx ?? MARGEN_IZQ;

  /** Misma compresión temporal que prepareSongForRendering / pentagrama por compás. */
  const highwayLayout = useMemo(
    () =>
      measureInfos.length > 0 ? buildHighwayTimeLayout(measureInfos, divisions, TRASTE_SPACING) : null,
    [measureInfos, divisions, TRASTE_SPACING]
  );

  const highwaySpan =
    measureInfos.length > 0 ? (highwayLayout?.totalContentWidth ?? totalWidth) : totalWidth;

  const useStaffGeom = Boolean(staffTimeGeometry && staffTimeGeometry.length > 0);
  const staffAnchorPx = useMemo(() => {
    if (!useStaffGeom || !staffTimeGeometry) return 0;
    return staffPixelXAtTime(staffTimeGeometry, scrollAnchorStartTime);
  }, [useStaffGeom, staffTimeGeometry, scrollAnchorStartTime]);

  /** Posición X en SVG del mástil (alineada al pentagrama si hay geometría). */
  const mastilNoteX = useMemo(() => {
    const sf = staffFirstNoteX ?? 0;
    if (useStaffGeom && staffTimeGeometry) {
      return (startTime: number) =>
        sf + staffPixelXAtTime(staffTimeGeometry, startTime) - staffAnchorPx;
    }
    const ox = staffFirstNoteX ? staffFirstNoteX - leftRailPx : 0;
    return (startTime: number) =>
      leftRailPx +
      ox +
      (highwayLayout
        ? highwayLayout.contentXAtDivision(fretboardVisualDivision(startTime, measureInfos))
        : (startTime / divisions) * TRASTE_SPACING) -
      highwayContentAnchorX;
  }, [
    useStaffGeom,
    staffTimeGeometry,
    staffFirstNoteX,
    staffAnchorPx,
    highwayLayout,
    divisions,
    TRASTE_SPACING,
    highwayContentAnchorX,
    measureInfos,
    leftRailPx,
  ]);

  const pieceEndMastilX = useMemo(() => {
    if (measureInfos.length === 0) return 0;
    const last = measureInfos[measureInfos.length - 1];
    return mastilNoteX(last.startTime + last.length);
  }, [measureInfos, mastilNoteX]);

  /**
   * El pentagrama VexFlow es más ancho que el highway temporal (padding por compás).
   * Si el SVG del mástil usa solo `highwaySpan`, el scroll del mástil se queda corto
   * ~varios compases antes del final mientras el pentagrama sigue avanzando.
   */
  const svgWidth = useMemo(() => {
    const highwayBaseline = leftRailPx + highwaySpan + MARGEN_DER;
    if (staffScrollContentWidth != null && staffScrollContentWidth > 0) {
      return Math.max(staffScrollContentWidth, highwayBaseline, pieceEndMastilX + MARGEN_DER, 700);
    }
    return Math.max(pieceEndMastilX + MARGEN_DER, highwayBaseline, 700);
  }, [leftRailPx, highwaySpan, staffScrollContentWidth, pieceEndMastilX]);

  /** Cejuela: borde entre margen (clave/compás) y vías; coincide con `notationLeadPx` del pentagrama. */
  const nutX = leftRailPx;
  const stringStartX = nutX + 2;
  /** Etiquetas de cuerda dentro del margen, sin invadir la zona rítmica. */
  const stringLabelCx = Math.max(14, Math.min(28, leftRailPx * 0.28));

  const getCuerdaY = (stringNum: number) =>
    MARGEN_TOP + (stringNum - 0.5) * CUERDA_SPACING;

  const sortedAttackTimes = useMemo(
    () => [...new Set(notes.map((n) => n.startTime))].sort((a, b) => a - b),
    [notes]
  );

  /** Pastillas con escala visual relativa (minPx y potencia γ); centradas en cada hueco del pentagrama. */
  const mastilRelOpts = useMemo(() => {
    const scale = TRASTE_SPACING / 80;
    return {
      minPx: Math.round(34 * scale),
      maxPx: Math.round(108 * scale),
      gamma: 0.88,
      gapPx: Math.max(3, Math.round(5 * scale)),
    };
  }, [TRASTE_SPACING]);

  const mastilRelativeWidths = useMemo(
    () => computeMastilRelativeBlockWidths(notes, sortedAttackTimes, measureInfos, mastilRelOpts),
    [notes, sortedAttackTimes, measureInfos, mastilRelOpts]
  );

  const mastilBlockLayout = useMemo(
    () =>
      sortedAttackTimes.length > 0
        ? layoutMastilBlocksInStaffSlots(
            sortedAttackTimes,
            mastilRelativeWidths,
            mastilNoteX,
            measureInfos,
            mastilRelOpts.gapPx,
            notes
          )
        : new Map<number, { x: number; width: number }>(),
    [sortedAttackTimes, mastilRelativeWidths, mastilNoteX, measureInfos, mastilRelOpts.gapPx, notes]
  );

  const measureBarlineXs = useMemo(() => {
    if (measureInfos.length === 0) return [];
    const last = measureInfos[measureInfos.length - 1];
    return [
      ...measureInfos.map((m) => mastilNoteX(m.startTime)),
      mastilNoteX(last.startTime + last.length),
    ];
  }, [measureInfos, mastilNoteX]);

  const getNoteWidth = (duration: number) =>
    Math.max((duration / divisions) * TRASTE_SPACING, 20);

  const eventWidths = useMemo(() => {
    const map = new Map<number, number>();
    const times = [...new Set(notes.map((n) => n.startTime))].sort((a, b) => a - b);
    for (let i = 0; i < times.length; i++) {
      const start = times[i];
      const nextStart = i < times.length - 1 ? times[i + 1] : start + divisions;
      const eventDuration = nextStart - start;
      map.set(start, Math.max((eventDuration / divisions) * TRASTE_SPACING, 28));
    }
    return map;
  }, [notes, divisions, TRASTE_SPACING]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    let lastMin = 0;
    let lastMax = 0;
    const sync = () => {
      raf = 0;
      const minX = Math.max(0, el.scrollLeft - VIEWPORT_PAD_PX);
      const maxX = el.scrollLeft + el.clientWidth + VIEWPORT_PAD_PX;
      if (
        Math.abs(minX - lastMin) < VIEWPORT_SYNC_EPS_PX &&
        Math.abs(maxX - lastMax) < VIEWPORT_SYNC_EPS_PX
      ) {
        return;
      }
      lastMin = minX;
      lastMax = maxX;
      setViewRange({ minX, maxX });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(sync);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    sync();
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [svgWidth]);

  const stringSpan = useMemo(() => {
    const x1 = Math.max(stringStartX, viewRange.minX);
    const x2 = Math.min(svgWidth - MARGEN_DER, viewRange.maxX);
    return { x1, x2, visible: x2 > x1 + 8 };
  }, [stringStartX, svgWidth, viewRange.minX, viewRange.maxX]);

  const visibleBarlineXs = useMemo(
    () => measureBarlineXs.filter((x) => x >= viewRange.minX - 24 && x <= viewRange.maxX + 24),
    [measureBarlineXs, viewRange.minX, viewRange.maxX]
  );

  const visibleRestSlots = useMemo(() => {
    const restTimes = [...new Set(notes.filter((n) => n.isRest).map((n) => n.startTime))].sort(
      (a, b) => a - b
    );
    return restTimes
      .map((t) => {
        const x = mastilNoteX(t);
        const slot = mastilBlockLayout.get(t);
        const w =
          slot?.width ??
          Math.max(
            highwayLayout ? highwayLayout.blockWidthAtStartTime(t, notes) : eventWidths.get(t) ?? 30,
            28
          );
        return { t, x, w };
      })
      .filter((s) => s.x + s.w >= viewRange.minX && s.x <= viewRange.maxX);
  }, [
    notes,
    mastilNoteX,
    mastilBlockLayout,
    highwayLayout,
    eventWidths,
    viewRange.minX,
    viewRange.maxX,
  ]);

  const visibleNoteItems = useMemo(() => {
    const items: Array<{
      note: PositionedNote;
      idx: number;
      x: number;
      y: number;
      w: number;
      h: number;
    }> = [];
    for (let idx = 0; idx < notes.length; idx++) {
      const note = notes[idx];
      if (note.isRest) continue;
      const slot = mastilBlockLayout.get(note.startTime);
      const x = slot?.x ?? mastilNoteX(note.startTime);
      const w =
        slot?.width ??
        (highwayLayout
          ? highwayLayout.blockWidthAtStartTime(note.startTime, notes)
          : eventWidths.get(note.startTime) ?? Math.max(getNoteWidth(note.duration), 20));
      if (x + w < viewRange.minX || x > viewRange.maxX) continue;
      items.push({
        note,
        idx,
        x,
        y: getCuerdaY(note.string),
        w,
        h: 20,
      });
    }
    return items;
  }, [
    notes,
    mastilBlockLayout,
    mastilNoteX,
    highwayLayout,
    eventWidths,
    viewRange.minX,
    viewRange.maxX,
    divisions,
    TRASTE_SPACING,
  ]);

  const woodGrainPattern = (
    <pattern id="woodGrain" width="200" height="200" patternUnits="userSpaceOnUse">
      <rect width="200" height="200" fill={PALETTE.fingerboard} />
      <path d="M0,20 Q60,15 120,22 T200,18" stroke={PALETTE.fingerboardLight} strokeWidth="0.8" fill="none" opacity="0.12" />
      <path d="M0,60 Q80,55 160,62 T200,58" stroke={PALETTE.fingerboardLight} strokeWidth="0.6" fill="none" opacity="0.1" />
      <path d="M0,100 Q50,95 100,102 T200,98" stroke={PALETTE.fingerboardLight} strokeWidth="0.7" fill="none" opacity="0.08" />
      <path d="M0,140 Q70,135 140,142 T200,138" stroke={PALETTE.fingerboardLight} strokeWidth="0.5" fill="none" opacity="0.1" />
      <path d="M0,180 Q90,175 180,182 T200,178" stroke={PALETTE.fingerboardLight} strokeWidth="0.6" fill="none" opacity="0.09" />
      <circle cx="40" cy="40" r="0.8" fill={PALETTE.fingerboardLight} opacity="0.06" />
      <circle cx="160" cy="80" r="1" fill={PALETTE.fingerboardLight} opacity="0.05" />
      <circle cx="80" cy="160" r="0.9" fill={PALETTE.fingerboardLight} opacity="0.06" />
      <circle cx="180" cy="30" r="0.7" fill={PALETTE.fingerboardLight} opacity="0.04" />
    </pattern>
  );

  return (
    <>
      <style>{`
        .highway-scroll::-webkit-scrollbar { display: none; }
        .highway-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
          contain: paint;
          overflow-anchor: none;
          -webkit-overflow-scrolling: touch;
        }
        .highway-scroll-active .highway-note-glow {
          filter: none !important;
        }
        .highway-scroll-active .mastil-note-interactive {
          pointer-events: none;
        }
        .highway-scroll-active .mastil-string-detail {
          display: none;
        }
        .highway-scroll-active .mastil-rest-ghost {
          opacity: 0.35;
        }
      `}</style>
      <div
        ref={scrollRef}
        className="w-full overflow-x-auto overflow-y-hidden highway-scroll"
        style={{
          backgroundColor: darkMode ? "transparent" : PALETTE.bgPanel,
        }}
      >
      <svg
        width={svgWidth}
        height={mastilHeight}
        style={{ display: "block", minWidth: svgWidth }}
        className="select-none"
      >
        <defs>
          {woodGrainPattern}

          <linearGradient id="fingerboardEdge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={PALETTE.fingerboardDark} />
            <stop offset="8%" stopColor={PALETTE.fingerboard} />
            <stop offset="92%" stopColor={PALETTE.fingerboard} />
            <stop offset="100%" stopColor={PALETTE.fingerboardDark} />
          </linearGradient>

          <linearGradient id="plainStringHi" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6e6a60" />
            <stop offset="35%" stopColor="#dcd8ce" />
            <stop offset="50%" stopColor="#f2efe8" />
            <stop offset="65%" stopColor="#c9c5ba" />
            <stop offset="100%" stopColor="#5c5952" />
          </linearGradient>

          <pattern id="woundStringTex" width="5" height="5" patternUnits="userSpaceOnUse">
            <rect width="5" height="5" fill="#7a4018" />
            <path
              d="M0,0 L5,5 M2.5,0 L2.5,5 M0,2.5 L5,2.5"
              stroke="#c58a4a"
              strokeWidth="0.38"
              opacity="0.75"
            />
            <path d="M0,5 L5,0" stroke="#4a260e" strokeWidth="0.22" opacity="0.45" />
          </pattern>

          <linearGradient id="woundStringBody" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6b3510" />
            <stop offset="25%" stopColor="#b87333" />
            <stop offset="50%" stopColor="#e8b87a" />
            <stop offset="75%" stopColor="#9a5220" />
            <stop offset="100%" stopColor="#5c2808" />
          </linearGradient>

          <filter id="pearlGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>

          <filter id="activeGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feFlood floodColor={PALETTE.inlayGold} floodOpacity="0.5" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="activeRedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feFlood floodColor={SATURATED_DEGREE_HEX[0]} floodOpacity="0.55" result="color" />
            <feComposite in="color" in2="blur" operator="in" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          <filter id="noteShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.35" />
          </filter>

          <linearGradient id="pearlGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FAF6F0" />
            <stop offset="40%" stopColor="#F0EAE0" />
            <stop offset="60%" stopColor="#E8E2D6" />
            <stop offset="100%" stopColor="#DDD5C8" />
          </linearGradient>

          <radialGradient id="dotPearl" cx="40%" cy="35%" r="60%">
            <stop offset="0%" stopColor="#FAF6F0" />
            <stop offset="70%" stopColor="#E0D8CC" />
            <stop offset="100%" stopColor="#C8BAA8" />
          </radialGradient>
        </defs>

        {/* FONDO DEL DIAPASÓN — desde el borde rítmico (tras la “pausa clave/compás”). */}
        {darkMode ? (
          <rect
            x={leftRailPx}
            y={MARGEN_TOP - 10}
            width={svgWidth - leftRailPx - MARGEN_DER}
            height={CUERDAS * CUERDA_SPACING + 20}
            fill="#11100f"
            rx={4}
          />
        ) : (
          <>
            <rect
              x={leftRailPx}
              y={MARGEN_TOP - 10}
              width={svgWidth - leftRailPx - MARGEN_DER}
              height={CUERDAS * CUERDA_SPACING + 20}
              fill="url(#woodGrain)"
              rx={6}
            />
            <rect
              x={leftRailPx}
              y={MARGEN_TOP - 10}
              width={svgWidth - leftRailPx - MARGEN_DER}
              height={CUERDAS * CUERDA_SPACING + 20}
              fill="url(#fingerboardEdge)"
              rx={6}
              opacity={0.3}
            />
          </>
        )}

        {/* Líneas verticales de compás */}
        {visibleBarlineXs.map((xAbs, i) => {
          const x = xAbs;
          const isFirst = i === 0;
          return (
            <line
              key={`compas-${i}`}
              x1={x}
              y1={MARGEN_TOP - 6}
              x2={x}
              y2={MARGEN_TOP + CUERDAS * CUERDA_SPACING + 6}
              stroke={isFirst ? (darkMode ? "rgba(255,255,255,0.15)" : PALETTE.barlineStrong) : (darkMode ? "rgba(255,255,255,0.08)" : PALETTE.barline)}
              strokeWidth={isFirst ? 1.5 : 1}
              strokeDasharray={isFirst ? "none" : "3,4"}
              opacity={isFirst ? (darkMode ? 0.5 : 0.7) : (darkMode ? 0.25 : 0.4)}
            />
          );
        })}

        {/* Marcadores de posición nácar */}
        {[3, 5, 7, 9, 12, 15].map((compasNum) => {
          const idx = compasNum - 1;
          if (idx < 0 || idx >= measureInfos.length) return null;
          const m = measureInfos[idx];
          const wM = highwayLayout?.measureVisualWidths[idx] ?? measureWidthPx;
          const x = mastilNoteX(m.startTime) + wM / 2;
          if (x > svgWidth - MARGEN_DER) return null;
          if (x < viewRange.minX - 32 || x > viewRange.maxX + 32) return null;
          const isDouble = compasNum === 12;
          const cy1 = MARGEN_TOP + (CUERDAS * CUERDA_SPACING) / 2 - (isDouble ? CUERDA_SPACING : 0);
          const cy2 = isDouble ? MARGEN_TOP + (CUERDAS * CUERDA_SPACING) / 2 + CUERDA_SPACING : null;
          return (
            <g key={`marker-${compasNum}`}>
              <circle cx={x} cy={cy1} r={darkMode ? 3 : 5} fill={darkMode ? "rgba(255,255,255,0.12)" : "url(#dotPearl)"} opacity={0.6} className={darkMode ? undefined : "highway-note-glow"} filter={darkMode ? undefined : "url(#pearlGlow)"} />
              {cy2 && (
                <circle cx={x} cy={cy2} r={darkMode ? 3 : 5} fill={darkMode ? "rgba(255,255,255,0.12)" : "url(#dotPearl)"} opacity={0.6} className={darkMode ? undefined : "highway-note-glow"} filter={darkMode ? undefined : "url(#pearlGlow)"} />
              )}
            </g>
          );
        })}

        {/* CEJUELA — diagrama de referencia (vista lateral esquemática, sin “3D” decorativo) */}
        {(() => {
          const y0 = MARGEN_TOP - 10;
          const y1 = MARGEN_TOP + CUERDAS * CUERDA_SPACING + 10;
          const ticks = [1, 2, 3, 4, 5, 6].map((stringNum) => getCuerdaY(stringNum));
          return (
            <g aria-label="Cejuela">
              <line
                x1={nutX}
                y1={y0}
                x2={nutX}
                y2={y1}
                stroke={darkMode ? "rgba(255,255,255,0.16)" : "rgba(60,52,46,0.35)"}
                strokeWidth={1.25}
                strokeLinecap="square"
              />
              <line
                x1={nutX + 0.6}
                y1={y0}
                x2={nutX + 0.6}
                y2={y1}
                stroke={darkMode ? "rgba(255, 77, 77, 0.55)" : "rgba(184,149,106,0.85)"}
                strokeWidth={0.9}
                strokeLinecap="square"
                opacity={darkMode ? 0.9 : 0.75}
              />
              {ticks.map((y, i) => (
                <g key={`nut-tick-${i}`}>
                  <line
                    x1={nutX - 7}
                    y1={y}
                    x2={nutX + 12}
                    y2={y}
                    stroke={darkMode ? "rgba(255,255,255,0.11)" : "rgba(60,52,46,0.2)"}
                    strokeWidth={1.05}
                    strokeLinecap="round"
                  />
                  <circle
                    cx={nutX}
                    cy={y}
                    r={darkMode ? 2.1 : 2.4}
                    fill={darkMode ? "rgba(255,255,255,0.08)" : "rgba(250,246,240,0.95)"}
                    stroke={darkMode ? "rgba(255,255,255,0.32)" : "rgba(90,80,72,0.35)"}
                    strokeWidth={0.55}
                  />
                </g>
              ))}
            </g>
          );
        })()}

        {/* CUERDAS: solo tramo visible + núcleo en scroll */}
        {stringSpan.visible &&
          Array.from({ length: CUERDAS }, (_, i) => {
          const stringNum = i + 1;
          const y = getCuerdaY(stringNum);
          const spec = STRING_MATERIALS[i];
          const x1 = stringSpan.x1;
          const x2 = stringSpan.x2;
          const wound = spec.wound;

          if (darkMode) {
            const core = wound ? "rgba(210,150,95,0.42)" : "rgba(255,255,255,0.14)";
            const rim = wound ? 2.35 : 1.35;
            return (
              <g key={`string-${stringNum}`}>
                <line
                  className="mastil-string-detail"
                  x1={x1}
                  y1={y + 1.2}
                  x2={x2}
                  y2={y + 1.2}
                  stroke="rgba(0,0,0,0.55)"
                  strokeWidth={spec.shadow}
                  opacity={0.45}
                  strokeLinecap="round"
                />
                <line
                  className="mastil-string-core"
                  x1={x1}
                  y1={y}
                  x2={x2}
                  y2={y}
                  stroke={core}
                  strokeWidth={rim}
                  strokeLinecap="round"
                />
                {!wound && (
                  <line
                    className="mastil-string-detail"
                    x1={x1}
                    y1={y - 0.35}
                    x2={x2}
                    y2={y - 0.35}
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth={0.55}
                    strokeLinecap="round"
                    opacity={0.9}
                  />
                )}
                {wound && (
                  <line
                    className="mastil-string-detail"
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke="rgba(240,190,130,0.22)"
                    strokeWidth={Math.max(1.1, rim * 0.55)}
                    strokeLinecap="round"
                    strokeDasharray="0.4,2.2"
                    opacity={0.85}
                  />
                )}
              </g>
            );
          }

          return (
            <g key={`string-${stringNum}`}>
              <line
                className="mastil-string-detail"
                x1={x1}
                y1={y + 1.5}
                x2={x2}
                y2={y + 1.5}
                stroke={PALETTE.stringShadow}
                strokeWidth={spec.shadow}
                opacity={0.42}
                strokeLinecap="round"
              />
              {wound ? (
                <>
                  <line
                    className="mastil-string-core"
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke="url(#woundStringBody)"
                    strokeWidth={spec.stroke}
                    strokeLinecap="round"
                  />
                  <line
                    className="mastil-string-detail"
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke="url(#woundStringTex)"
                    strokeWidth={spec.stroke * 0.92}
                    strokeLinecap="round"
                    opacity={0.55}
                  />
                  <line
                    className="mastil-string-detail"
                    x1={x1}
                    y1={y - 0.45}
                    x2={x2}
                    y2={y - 0.45}
                    stroke="#f4e0c8"
                    strokeWidth={0.55}
                    strokeLinecap="round"
                    opacity={0.28}
                  />
                </>
              ) : (
                <>
                  <line
                    className="mastil-string-core"
                    x1={x1}
                    y1={y}
                    x2={x2}
                    y2={y}
                    stroke="url(#plainStringHi)"
                    strokeWidth={spec.stroke}
                    strokeLinecap="round"
                  />
                  <line
                    className="mastil-string-detail"
                    x1={x1}
                    y1={y - 0.42}
                    x2={x2}
                    y2={y - 0.42}
                    stroke="#ffffff"
                    strokeWidth={0.38}
                    strokeLinecap="round"
                    opacity={0.5}
                  />
                </>
              )}
            </g>
          );
        })}

        {/* ETIQUETAS DE CUERDAS AL AIRE */}
        {Array.from({ length: CUERDAS }, (_, i) => {
          const stringNum = i + 1;
          const y = getCuerdaY(stringNum);
          const specLoop = STRING_MATERIALS[i];
          const labelStroke = specLoop.wound ? PALETTE.stringBronze : PALETTE.stringSteel;

          return (
            <g key={`label-${stringNum}`}>
              <circle
                cx={stringLabelCx}
                cy={y}
                r={darkMode ? 10 : 15}
                fill={darkMode ? "rgba(255,255,255,0.06)" : PALETTE.nut}
                stroke={darkMode ? "rgba(255,255,255,0.12)" : labelStroke}
                strokeWidth={1.5}
                opacity={0.95}
              />
              <text
                x={stringLabelCx}
                y={y + (darkMode ? 4 : 5)}
                textAnchor="middle"
                fontSize={darkMode ? 11 : 13}
                fontWeight={darkMode ? 600 : 700}
                fill={darkMode ? "rgba(255,255,255,0.7)" : PALETTE.noteText}
                fontFamily={darkMode ? "'Inter', sans-serif" : "'Playfair Display', serif"}
              >
                {OPEN_NOTES[i]}
              </text>
              {darkMode && (
                <text
                  x={stringLabelCx}
                  y={y + 12}
                  textAnchor="middle"
                  fontSize={5.5}
                  fontWeight={500}
                  fill="rgba(200, 230, 255, 0.42)"
                  fontFamily="'Inter', sans-serif"
                  letterSpacing="0.06em"
                >
                  aire
                </text>
              )}
            </g>
          );
        })}

        {/* SILENCIOS — marcador fantasma translúcido en cada cuerda/posición
            para que el diapasón no aparezca vacío durante compases de silencio. */}
        {visibleRestSlots.map(({ t, x, w }) =>
            Array.from({ length: CUERDAS }, (_, si) => {
              const stringNum = si + 1;
              const y = getCuerdaY(stringNum);
              const h = 16;
              return (
                <rect
                  key={`rest-${t}-s${stringNum}`}
                  className="mastil-rest-ghost"
                  x={x}
                  y={y - h / 2}
                  width={w - 4}
                  height={h}
                  rx={h / 2}
                  fill="none"
                  stroke={darkMode ? "rgba(255,255,255,0.06)" : "rgba(90,80,72,0.08)"}
                  strokeWidth={1}
                  strokeDasharray="3,4"
                />
              );
            })
        )}

        {/* NOTAS — solo ventana visible */}
        {visibleNoteItems.map(({ note, idx, x, y, w, h }) => {
          const resolvedActiveTime =
            activeStartTime >= 0
              ? activeStartTime
              : -1;
          const isActive = activeStartTime >= 0 && note.startTime === resolvedActiveTime;
          const isHovered = onNoteClick ? hoveredNote === idx : false;
          const noteHandlers = onNoteClick
            ? {
                onMouseEnter: () => setHoveredNote(idx),
                onMouseLeave: () => setHoveredNote(null),
                onClick: () => onNoteClick(note, idx),
              }
            : {};

          return (
            <g
              key={`note-${idx}`}
              className={onNoteClick ? "mastil-note-interactive" : undefined}
              style={{ cursor: onNoteClick ? "pointer" : "default" }}
              {...noteHandlers}
            >
              {isActive && !darkMode && (
                  <rect
                    x={x - 4}
                    y={y - h / 2 - 4}
                    width={w + 8}
                    height={h + 8}
                    rx={(h + 8) / 2}
                    fill="none"
                    stroke={PALETTE.inlayGold}
                    strokeWidth={2.25}
                    opacity={0.85}
                    className="highway-note-glow"
                    filter="url(#activeGlow)"
                  />
              )}

              {!darkMode && isHovered && !isActive && (
                <rect
                  x={x - 2}
                  y={y - h / 2 - 2}
                  width={w + 4}
                  height={h + 4}
                  rx={(h + 4) / 2}
                  fill="none"
                  stroke={PALETTE.inlayGold}
                  strokeWidth={1.35}
                  opacity={0.45}
                />
              )}

              <rect
                x={x + 1.5}
                y={y - h / 2 + 2}
                width={w}
                height={h}
                rx={h / 2}
                fill={PALETTE.noteShadow}
                opacity={0.38}
              />

              {darkMode ? (
                <>
                  {/* Dark mode: high-contrast thin pill con color de grado */}
                  {(() => {
                    const deg = note.degree ? note.degree - 1 : -1;
                    const degColor = deg >= 0 && deg < 7 ? DEGREE_COLORS[deg] : "#888888";
                    const glowFilter = isActive ? "url(#activeRedGlow)" : undefined;
                    const isOpenString = note.fret === 0;
                    return (
                      <>
                        {isOpenString && (
                          <rect
                            x={x - 2.5}
                            y={y - h / 2 - 2.5}
                            width={w + 5}
                            height={h + 5}
                            rx={(h + 5) / 2}
                            fill="none"
                            stroke="rgba(165, 220, 255, 0.42)"
                            strokeWidth={1.15}
                            strokeDasharray="4 5"
                            opacity={0.95}
                          />
                        )}
                        {isActive && (
                          <>
                            <rect
                              x={x - 3}
                              y={y - h / 2 - 3}
                              width={w + 6}
                              height={h + 6}
                              rx={(h + 6) / 2}
                              fill={degColor}
                              opacity={0.22}
                              className={glowFilter ? "highway-note-glow" : undefined}
                              filter={glowFilter}
                            />
                            <rect
                              x={x - 3}
                              y={y - h / 2 - 3}
                              width={w + 6}
                              height={h + 6}
                              rx={(h + 6) / 2}
                              fill="none"
                              stroke={degColor}
                              strokeWidth={2}
                              opacity={0.68}
                              className={glowFilter ? "highway-note-glow" : undefined}
                              filter={glowFilter}
                            />
                          </>
                        )}
                        <rect
                          x={x + 1}
                          y={y - h / 2 + 1}
                          width={w - 2}
                          height={h - 2}
                          rx={(h - 2) / 2}
                          fill={
                            isOpenString
                              ? "rgba(200, 240, 255, 0.12)"
                              : isActive
                                ? degColor
                                : "rgba(255,255,255,0.06)"
                          }
                          stroke={
                            isActive
                              ? degColor
                              : isOpenString
                                ? "rgba(175, 225, 255, 0.55)"
                                : isHovered
                                  ? degColor
                                  : deg >= 0
                                    ? `${degColor}88`
                                    : "rgba(255,255,255,0.12)"
                          }
                          strokeWidth={isActive ? 1.5 : isOpenString ? 1.35 : 1}
                        />
                        <text
                          x={x + w / 2}
                          y={y + (h > 18 ? 4 : 3.5)}
                          textAnchor="middle"
                          fontSize={isOpenString ? (w > 42 ? 14 : w > 32 ? 12.5 : 11.5) : w > 42 ? 13 : w > 32 ? 11 : 10}
                          fontWeight={isActive ? 700 : 600}
                          fill={isActive ? "#ffffff" : isOpenString ? "rgba(230, 248, 255, 0.96)" : "rgba(255,255,255,0.9)"}
                          fontFamily="'Inter', sans-serif"
                          style={{ letterSpacing: "0.02em" }}
                        >
                          {note.fretLabel}
                        </text>
                        {isOpenString && (
                          <text
                            x={x + w / 2}
                            y={y + (h > 18 ? 12 : 11)}
                            textAnchor="middle"
                            fontSize={6.2}
                            fontWeight={600}
                            fill="rgba(190, 235, 255, 0.78)"
                            fontFamily="'Inter', sans-serif"
                            letterSpacing="0.14em"
                            style={{ textTransform: "uppercase" }}
                          >
                            al aire
                          </text>
                        )}
                        {note.leftHandFingering && (
                          <g>
                            <circle
                              cx={x + w / 2}
                              cy={y - h / 2 - 10}
                              r={7}
                              fill={degColor}
                              opacity={0.9}
                            />
                            <text
                              x={x + w / 2}
                              y={y - h / 2 - 7}
                              textAnchor="middle"
                              fontSize={9}
                              fontWeight={700}
                              fill="#ffffff"
                              fontFamily="'Inter', sans-serif"
                            >
                              {note.leftHandFingering}
                            </text>
                          </g>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <>
                  <rect
                    x={x}
                    y={y - h / 2}
                    width={w}
                    height={h}
                    rx={h / 2}
                    fill="url(#pearlGradient)"
                    stroke={
                      isActive ? PALETTE.noteBorderActive : isHovered ? PALETTE.inlayGold : PALETTE.noteBorder
                    }
                    strokeWidth={isActive ? 2.5 : 1.5}
                    className="highway-note-glow"
                    filter="url(#noteShadow)"
                  />
                  <ellipse
                    cx={x + w * 0.38}
                    cy={y - h * 0.2}
                    rx={w * 0.22}
                    ry={h * 0.18}
                    fill="#FFF"
                    opacity={0.35}
                  />
                  <text
                    x={x + w / 2}
                    y={y + 5}
                    textAnchor="middle"
                    fontSize={w > 42 ? 15 : w > 32 ? 13 : 11}
                    fontWeight={700}
                    fill={isActive ? PALETTE.noteTextActive : PALETTE.noteText}
                    fontFamily="'Playfair Display', serif"
                    style={{ letterSpacing: "0.02em" }}
                  >
                    {note.fretLabel}
                  </text>
                </>
              )}

              {note.duration <= divisions / 2 && (
                <rect
                  x={x + w - 7}
                  y={y - h / 2 + 5}
                  width={2.5}
                  height={h - 10}
                  rx={1.25}
                  fill={PALETTE.inlayGold}
                  opacity={0.6}
                />
              )}
            </g>
          );
        })}

        {/* PLAYHEAD (omitido si el padre dibuja playhead fijo en el viewport) */}
        {!hidePlayhead && currentNoteIndex >= 0 && notes[currentNoteIndex] && (
          <g>
            {(() => {
              const note = notes[currentNoteIndex];
              const x = mastilNoteX(note.startTime);
              return (
                <>
                  <line
                    x1={x - 1}
                    y1={MARGEN_TOP - 16}
                    x2={x - 1}
                    y2={MARGEN_TOP + CUERDAS * CUERDA_SPACING + 16}
                    stroke="#c9405a"
                    strokeWidth={2.5}
                    opacity={1}
                  />
                  <polygon
                    points={`${x - 7},${MARGEN_TOP - 16} ${x + 5},${MARGEN_TOP - 16} ${x - 1},${MARGEN_TOP - 6}`}
                    fill="#c9405a"
                  />
                </>
              );
            })()}
          </g>
        )}


      </svg>
    </div>
    </>
  );
};

const MemoSongFretboardFlow = React.memo(SongFretboardFlow);
export { MemoSongFretboardFlow as SongFretboardFlow };
export default MemoSongFretboardFlow;
